import ReportSendSpecimen from "issara-sdk/apis/ReportSendSpecimen_apps_LAB";
import OfficialAccountDivisionDetail from "issara-sdk/apis/OfficialAccountDivisionDetail_apps_PRXM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  downloadXLSX,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  convertBEDateToADDashDate,
  formatDate,
  formatUTCtoBEString,
} from "react-lib/utils/dateUtils";
import { fetchURLToBase64 } from "react-lib/utils/printURL";
import moment from "moment";

import FormSendSpecimen from "react-lib/apps/MEH/pdfForm/FormSendSpecimen";
import getPdfMake from "react-lib/appcon/common/pdfMake";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  PRINT: "PRINT",
  DOWNLOAD_XLSX: "DOWNLOAD_XLSX",
} as const;

export const SEQUENCE_NAME = "MeReportList" as const;
export const MAIN_CARD_NAME = "CardMeReportList" as const;
const DEFAULT_LIMIT = 20;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  django?: Record<string, any>;
  ReportListSequence: ReportListType;
}>;

export type ReportListType = Partial<{
  sequenceIndex?: "Action" | "START" | null;
  reportListFilter?: {
    hn?: string;
    first_name?: string;
    last_name?: string;
    start_date?: string;
    end_date?: string;
    vendor?: string;
  };
  currentPage?: number;
  limit?: number;
  totalPage?: number;
  reportList: Record<string, any>[];
}> | null;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number; masterData?: any };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: {
    currentPage?: number;
    limit?: number;
  };
  [ACTIONS.PRINT]: {};
  [ACTIONS.DOWNLOAD_XLSX]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

export type OptionType = {
  key: string;
  text: string;
  value: string;
};

export type OptionsState = {
  underlyingDisease: OptionType[];
  supplementData: OptionType[];
};

/** ===================== Constants ====================  */
export const StateInitial: State = {
  ReportListSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const Masters = [
  ["province", {}],
  ["district", {}],
  ["city", {}],
  ["vendorList", {}],
] as const;

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const date = formatDate(moment());
  const oldSequence = state.ReportListSequence;
  const oldFilter = oldSequence?.reportListFilter;

  controller.setState(
    {
      ReportListSequence: {
        ...oldSequence,
        sequenceIndex: "Action",
        reportListFilter: {
          ...oldFilter,
          hn: oldFilter?.hn ?? "",
          first_name: oldFilter?.first_name ?? "",
          last_name: oldFilter?.last_name ?? "",
          start_date: oldFilter?.start_date || date,
          end_date: oldFilter?.end_date || date,
          vendor: oldFilter?.vendor ?? "ALL",
        },
        currentPage: oldSequence?.currentPage ?? 0,
        limit: oldSequence?.limit ?? DEFAULT_LIMIT,
        totalPage: oldSequence?.totalPage ?? 1,
      },
    },
    () =>
      Action(controller, {
        action: ACTIONS.SEARCH,
        ...params,
      }),
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearchReportSendSpecimen,
    [ACTIONS.PRINT]: HandlePrintReportSendSpecimen,
    [ACTIONS.DOWNLOAD_XLSX]: HandleDownloadXLSX,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */

const HandleSearchReportSendSpecimen: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING,
    },
  });

  const reportListFilter = state.ReportListSequence?.reportListFilter;
  const limit = params?.limit ?? state.ReportListSequence?.limit ?? DEFAULT_LIMIT;
  const currentPage = params?.currentPage ?? state.ReportListSequence?.currentPage ?? 0;

  const data = Object.fromEntries(
    Object.entries({
      ...reportListFilter,
      vendor: reportListFilter?.vendor === "ALL" ? "" : reportListFilter?.vendor,
      export_excel: false,
      division: controller.data.division,
      limit,
      offset: currentPage * limit,
      start_date: convertBEDateToADDashDate(reportListFilter?.start_date),
      end_date: convertBEDateToADDashDate(reportListFilter?.end_date),
    }).filter(([_, value]) => value !== "" && value != null),
  );

  const [reportSendSpecimenRes, reportSendSpecimenErr, reportSendSpecimenNet] =
    await ReportSendSpecimen.get({
      params: data,
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
      },
    });

  if (reportSendSpecimenRes) {
    controller.setState({
      ReportListSequence: {
        ...state.ReportListSequence,
        reportList: reportSendSpecimenRes?.items || [],
        currentPage,
        limit,
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR,
      },
    });
  }
};

const HandlePrintReportSendSpecimen: Handler<Params<"PRINT">> = async (controller, params) => {
  const state = controller.getState();
  const masterData = controller.data.masterData;
  const reportListFilter = state.ReportListSequence?.reportListFilter;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PRINT]: BLC_STATUS.LOADING,
    },
  });

  const limit = params?.limit ?? state.ReportListSequence?.limit ?? DEFAULT_LIMIT;
  const currentPage = params?.currentPage ?? state.ReportListSequence?.currentPage ?? 0;

  const data = Object.fromEntries(
    Object.entries({
      ...reportListFilter,
      vendor: reportListFilter?.vendor === "ALL" ? "" : reportListFilter?.vendor,
      export_excel: false,
      division: controller.data.division,
      limit,
      offset: currentPage * limit,
      start_date: convertBEDateToADDashDate(reportListFilter?.start_date),
      end_date: convertBEDateToADDashDate(reportListFilter?.end_date),
    }).filter(([_, value]) => value !== "" && value != null),
  );

  const [reportSendSpecimenRes, reportSendSpecimenErr, reportSendSpecimenNet] =
    await ReportSendSpecimen.get({
      params: data,
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
      },
    });

  if (reportSendSpecimenRes) {
    let oaDetail = {};

    const [oaDetailRes, oaDetailErr, oaDetailNet] = await OfficialAccountDivisionDetail.retrieve({
      params: {},
      extra: { division: controller.data.division || 0 },
      apiToken: controller.apiToken,
    });

    if (oaDetailRes) {
      let imageFile = "";

      if (oaDetailRes?.image_file) {
        imageFile = await fetchURLToBase64(oaDetailRes?.image_file || "");
      }

      const province = (masterData?.province || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_province,
      );
      const district = (masterData?.district || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_district,
      );
      const city = (masterData?.city || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_city,
      );

      oaDetail = {
        name: oaDetailRes?.accounting_business_name || "",
        addressTH: oaDetailRes?.accounting_full_address_text || "",
        addressEN: `${city?.name_en || ""} ${
          district?.name_en || ""
        } ${province?.name_en || ""} ${city?.zipcode || ""}`,
        taxPayerID: oaDetailRes?.accounting_tax_payer_id || "",
        imageFile,
      };
    }

    const toDate = moment();

    const data = Object.assign({
      oaDetail,
      startDate: reportListFilter?.start_date,
      endDate: reportListFilter?.end_date,
      reportSendSpecimen: reportSendSpecimenRes?.items || [],
      printedBy: state.django?.user?.full_name || "",
      printDate: formatUTCtoBEString(toDate)?.[0],
      printTime: formatUTCtoBEString(toDate)?.[1],
      division: state.selectedDivision?.name,
      divisionCode: state.selectedDivision?.code,
    });

    let docDef: any = { content: [] };

    docDef = await FormSendSpecimen(data);

    const pdfMake = (await getPdfMake(true)).createPdf(docDef);
    pdfMake.open();

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT]: BLC_STATUS.ERROR,
      },
    });
  }
};

const HandleDownloadXLSX: Handler<Params<"DOWNLOAD_XLSX">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.DOWNLOAD_XLSX]: BLC_STATUS.LOADING,
    },
  });

  const reportListFilter = state.ReportListSequence?.reportListFilter;
  const limit = params?.limit ?? state.ReportListSequence?.limit ?? DEFAULT_LIMIT;
  const currentPage = params?.currentPage ?? state.ReportListSequence?.currentPage ?? 0;

  const data = Object.fromEntries(
    Object.entries({
      ...reportListFilter,
      vendor: reportListFilter?.vendor === "ALL" ? "" : reportListFilter?.vendor,
      export_excel: true,
      division: controller.data.division,
      limit,
      offset: currentPage * limit,
      start_date: convertBEDateToADDashDate(reportListFilter?.start_date),
      end_date: convertBEDateToADDashDate(reportListFilter?.end_date),
    }).filter(([_, value]) => value !== "" && value != null),
  );

  const [reportSendSpecimenRes, reportSendSpecimenErr, reportSendSpecimenNet] =
    await ReportSendSpecimen.get({
      params: data,
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
        responseType: "arraybuffer",
      },
    });

  if (reportSendSpecimenRes) {
    downloadXLSX(reportSendSpecimenRes, "รายงานบันทึกการส่งสิ่งส่งตรวจ");

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.DOWNLOAD_XLSX]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.DOWNLOAD_XLSX]: BLC_STATUS.ERROR,
      },
    });
  }
};

const modData = [
  {
    collect_specimen_time: "2025-05-19T10:38:00+07:00",
    patient_hn: "25000108",
    encounter_number: "305",
    patient_name: "Mr. Piroonwat Tripetch",
    specimen_name: "EDTA  Blood",
    send_specimen_time: "2025-05-19T10:38:00+07:00",
    send_specimen_user_name: "นาย เจ้าหน้าที่ Lab คนที่ 1",
    vendor_name: "",
    test_names: [
      "Platelet",
      "Platelet count",
      "RDW_CV",
      "Polychromasia",
      "Stomatocyte",
      "Tear drop cell",
      "Spherocyte",
      "Target cell",
      "Ovalocyte",
      "Schistocyte",
      "Poikilocytosis",
      "Macrocyte",
      "Microcyte",
      "Anisocytosis",
      "Hypochromia",
      "RBC mophology",
      "MCHC",
      "MCH",
      "MCV",
      "Hematocrite",
      "Hemoglobin",
      "RBC",
      "Basophil (%)",
      "Eosinophil (%)",
      "Monocyte (%)",
      "Lymphocyte (%)",
      "Neutrophil (%)",
      "WBC Count",
      "Complete Blood Count",
    ],
  },
  {
    collect_specimen_time: "2025-05-08T15:44:00+07:00",
    patient_hn: "25000108",
    encounter_number: "305",
    patient_name: "Mr. Piroonwat Tripetch",
    specimen_name: "Urine",
    send_specimen_time: "2025-05-08T15:45:00+07:00",
    send_specimen_user_name: "นาย เจ้าหน้าที่ Lab คนที่ 1",
    vendor_name: "",
    test_names: [
      "Ascorbic acid",
      "microalbumin-creatinine ratio",
      "Squamous epithelial",
      "RBC",
      "Bacteria",
      "Budding yeast",
      "Amorphous",
      "Mucous",
      "Triple phpsphate",
      "Uric",
      "Calcium oxalate",
      "RBC casts",
      "WBC",
      "Bladder epithelial",
      "WBC casts",
      "microalbumin",
      "creatinine",
      "calcium",
      "Broad casts",
      "Waxy casts",
      "Granular casts",
      "Cellular casts",
      "Hyaline casts",
      "Ketone",
      "Billirubin",
      "Total protein",
      "Nitrite",
      "Glucose",
      "Blood",
      "Urobillinogen",
      "Leukocyte",
      "pH",
      "Specific gravity",
      "Appearance",
      "Color",
      "Urinalysis",
    ],
  },
];
